import { Button, message, Modal } from 'antd';
import { useTranslate } from '@tolgee/react';
import { useCallback, memo, useState, useRef } from 'react';
import { useUpdateStockRule } from './connect';
import { FormOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs from 'lib/dayjs';
import { useClickAway } from 'react-use';
import { DatePicker } from 'components/DatePicker';

export const useCancelMark = () => {
  const { t } = useTranslate();
  const { update } = useUpdateStockRule();
  return useCallback(
    (ids: string[] | string, callback?: () => void) => {
      Modal.confirm({
        title: t('outofstock_cancel_confirm_title'),
        content: t('outofstock_cancel_confirm_content'),
        onOk: () => {
          update({
            stockRuleId: Array.isArray(ids) ? ids : [ids],
            status: 2,
          })
            .then(() => {
              message.success(t('outofstock_cancel_success'));
              callback?.();
            })
            .catch(() => {
              message.error(t('outofstock_update_retry'));
            });
        },
      });
    },
    [t, update],
  );
};

export const EditEstimatedAvailableTime = memo(
  (props: { value: string; id: string | string[]; noEdit?: boolean; onOk?: () => void }) => {
    const { t } = useTranslate();
    const { value, id, noEdit = false, onOk } = props;
    const [editMode, setEditMode] = useState(false);
    const [date, setDate] = useState(value);
    const editRef = useRef(null);
    const openRef = useRef(false);
    const { update, loading } = useUpdateStockRule();
    useClickAway(editRef, () => {
      if (editMode && !openRef.current) {
        setEditMode(false);
        update({
          stockRuleId: Array.isArray(id) ? id : [id],
          estimatedAvailableTime: date,
        })
          .then(() => {
            onOk?.();
          })
          .catch(() => {
            message.error(t('outofstock_update_retry'));
          });
      }
    });

    return editMode ? (
      <div className="w-full" ref={editRef}>
        <DatePicker
          disabledDate={(current) => {
            return current && current < dayjs();
          }}
          showToday={false}
          value={date ? dayjs(date, 'YYYY-MM-DD') : null}
          allowClear
          onChange={(date) => {
            setDate(date ? date?.format('YYYY-MM-DD') : '');
          }}
          onOpenChange={(open) => {
            openRef.current = open;
          }}
        />
      </div>
    ) : (
      <div className="w-full flex items-center justify-start">
        <span>{value || '-'}</span>
        {!noEdit && (
          <Button
            onClick={() => setEditMode(true)}
            type="text"
            icon={loading ? <LoadingOutlined /> : <FormOutlined />}
          ></Button>
        )}
      </div>
    );
  },
);
